import { Product } from '@/types'
import { ProductImageSize, ProductSkuAdditionalInfo } from '@/types/Product'
import { checkVariantNameIsSize } from '@/helpers/common'

const IMAGE_FALLBACK = 'https://i.zgallerie.com/'
const DEFAULT_SIZE = 'thumbnail'

const ProductHelpers = {
  name({ source: { name } }: { source: Product }) {
    return name.split('~', 1)[0]
  },
  thumbnail(product) {
    const images = product?.images || []
    return ((images.filter(({ url_thumbnail }) => url_thumbnail) || [])[0] || {}).url_thumbnail
      || IMAGE_FALLBACK
  },
  oversizeCharge({ product, sku }: { product: Product, sku: string }) {
    const charge = (product.additional_info?.[sku] as ProductSkuAdditionalInfo)?.oversize_charge
    return charge && parseFloat(charge).toFixed(2)
  },
  artistName({ product }: { product: Product }) {
    return product.additional_info?.artist_name
  },
  availableAtStores({ product, sku }: { product: Product, sku: string }) {
    return (product.additional_info?.[sku] as ProductSkuAdditionalInfo)?.is_available_at_store
  },
  freeShipping({ product }: { product: Product }) {
    return product.is_free_shipping
  },
  clearance({ product, sku }: { product: Product, sku: string }) {
    return (product.additional_info?.[sku] as ProductSkuAdditionalInfo)?.clearance
  },
  dimensions({ product, sku }: { product: Product, sku: string }) {
    return (product.additional_info?.[sku] as ProductSkuAdditionalInfo)?.dimensions
  },
  giftBox({ product, sku }: { product: Product, sku: string }) {
    return (product.additional_info?.[sku] as ProductSkuAdditionalInfo)?.gift_boxing
  },
  getCollectionVariants(product: Product) {
    const { options } = product
    let variants: Product['variants'] = []
    if (options.length === 1 && options[0].display_name === 'Collection') {
      variants = product.variants
    }

    if (!variants.length) {
      return []
    }

    if (variants.every(checkVariantNameIsSize)) {
      variants.sort((a, b) => (
        parseFloat(`${a.calculated_price || 0}`) - parseFloat(`${b.calculated_price || 0}`)
      ))
    } else {
      variants.sort((a, b) => {
        if (parseFloat(`${b.calculated_price || 0}`) > parseFloat(`${a.calculated_price || 0}`)) {
          return 1
        } if (parseFloat(`${a.calculated_price || 0}`) === parseFloat(`${b.calculated_price || 0}`)) {
          if (a.option_values[0].label > b.option_values[0].label) {
            return 1
          }
        }
        return -1
      })
    }

    return variants
  },
  getInitialProductAttribute(product: Product) {
    return product.options.reduce((accumulator: number[], option) => {
      accumulator[option.id] = option.option_values[0].id
      return accumulator
    }, [])
  },

  getProductAttributeBySku(product: Product, sku: string) {
    const optionValues = product.variants.find((variant) => variant.sku === sku)?.option_values
    return optionValues?.reduce((accumulator: number[], optionValue) => {
      accumulator[optionValue.option_id] = optionValue.id
      return accumulator
    }, []) || []
  },
}

export default ProductHelpers
